import React, { useRef, useState } from "react";

const EDGES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
const CURSORS = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
  move: "default",
};
// The strip along the top of a window that moves it (macOS's title bar / toolbar area), unless the
// window says how tall its own is (`titlebar`).
const TITLEBAR = 52;
const INTERACTIVE = "button, input, textarea, select, a, label, [role='button']";

const between = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Keeps a frame inside the workspace, shrinking it first if the workspace is smaller.
const fit = (f, ws, min) => {
  const w = Math.min(Math.max(f.w, min.w), ws.w);
  const h = Math.min(Math.max(f.h, min.h), ws.h);
  return { x: between(f.x, 0, ws.w - w), y: between(f.y, 0, ws.h - h), w, h };
};

// A movable, resizable window, positioned inside the workspace (the area between the menu bar
// and the Dock). `initial(workspace)` gives the default frame; once the person moves or resizes
// the window, their frame is kept, and pulled back on screen if the browser shrinks. Zoomed
// windows fill the workspace and can't be dragged.
const WindowFrame = ({
  workspace,
  initial,
  min,
  open,
  front,
  zoomed,
  onFocus,
  onToggleZoom,
  titlebar = TITLEBAR,
  children,
}) => {
  const [manual, setManual] = useState(null);
  const [live, setLive] = useState(null);
  const [dragEdge, setDragEdge] = useState(null);
  const liveRef = useRef(null);
  const lastTitlePress = useRef({ t: 0, x: 0, y: 0 });

  const base = fit(manual || initial(workspace), workspace, min);
  const frame = zoomed ? { x: 8, y: 4, w: workspace.w - 16, h: workspace.h - 8 } : live || base;

  const begin = (e, edge) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onFocus();
    const start = { px: e.clientX, py: e.clientY, f: base };
    const right = start.f.x + start.f.w;
    const bottom = start.f.y + start.f.h;

    const move = (ev) => {
      const dx = ev.clientX - start.px;
      const dy = ev.clientY - start.py;
      let { x, y, w, h } = start.f;
      if (edge === "move") {
        x = between(x + dx, 0, workspace.w - w);
        y = between(y + dy, 0, workspace.h - h);
      } else {
        // Each dragged edge moves on its own; the opposite edge stays put.
        if (edge.includes("w")) {
          x = between(start.f.x + dx, 0, right - min.w);
          w = right - x;
        }
        if (edge.includes("e")) w = between(start.f.w + dx, min.w, workspace.w - start.f.x);
        if (edge.includes("n")) {
          y = between(start.f.y + dy, 0, bottom - min.h);
          h = bottom - y;
        }
        if (edge.includes("s")) h = between(start.f.h + dy, min.h, workspace.h - start.f.y);
      }
      liveRef.current = { x, y, w, h };
      setLive(liveRef.current);
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      if (liveRef.current) setManual(liveRef.current);
      liveRef.current = null;
      setLive(null);
      setDragEdge(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    setDragEdge(edge);
  };

  const inTitlebar = (e) => {
    const top = e.currentTarget.getBoundingClientRect().top;
    return e.clientY - top < titlebar && !e.target.closest(INTERACTIVE);
  };

  return (
    <div
      className={`win ${front ? "is-front" : ""} ${dragEdge ? "is-dragging" : ""}`}
      style={{ left: frame.x, top: frame.y, width: frame.w, height: frame.h }}
      onPointerDown={(e) => {
        onFocus();
        if (!open || e.button !== 0 || !inTitlebar(e)) return;
        // A second press in the title bar within 400ms, near the first, is a double-click: zoom.
        // (Detected here rather than with dblclick, which Safari drops once a drag has begun.)
        const prev = lastTitlePress.current;
        const now = Date.now();
        if (now - prev.t < 400 && Math.abs(e.clientX - prev.x) < 5 && Math.abs(e.clientY - prev.y) < 5) {
          lastTitlePress.current = { t: 0, x: 0, y: 0 };
          e.preventDefault();
          onToggleZoom();
          return;
        }
        lastTitlePress.current = { t: now, x: e.clientX, y: e.clientY };
        if (!zoomed) begin(e, "move");
      }}
    >
      {children}
      {open &&
        !zoomed &&
        EDGES.map((edge) => (
          <span
            key={edge}
            className={`win-handle win-handle--${edge}`}
            style={{ cursor: CURSORS[edge] }}
            onPointerDown={(e) => begin(e, edge)}
            aria-hidden="true"
          />
        ))}
      {/* While dragging, a full-screen layer keeps the cursor steady and stops hover effects. */}
      {dragEdge && <div className="win-drag-layer" style={{ cursor: CURSORS[dragEdge] }} />}
    </div>
  );
};

export default WindowFrame;
